import React, { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "../styles/components/OrderHistory.css";

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const API_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => {
        setAccessToken(token);
      });

      if (user && user.email) {
        setUserEmail(user.email);
      }
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  // Function to format date from "2025-09-11 22:24:59" to "ddmmyy" format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(2);
    return `${day}.${month}.${year}`;
  };

  // Function to format amount from 156000 to "1 560,00 NOK"
  const formatAmount = (amount) => {
    const formatted = (amount / 100).toFixed(2).replace(".", ",");
    // Add spaces as thousand separators
    return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " NOK";
  };

  // Function to translate payment status
  const translatePaymentStatus = (status) => {
    const statusMap = {
      paid: "Betalt",
      pending: "Venter på betaling",
      failed: "Betaling feilet",
      refunded: "Refundert",
      canceled: "Kansellert",
    };
    return statusMap[status] || status;
  };

  // Fetch orders from the backend
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);

      if (!accessToken || !userEmail) {
        return; // Wait for token and email to be available
      }

      const response = await fetch(`${API_URL}/orders`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setOrders(data.orders || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Kunne ikke hente ordrehistorikk. Prøv igjen senere.");
    } finally {
      setLoading(false);
    }
  }, [API_URL, accessToken, userEmail]);

  // Download PDF function
  const downloadPDF = async (orderId) => {
    try {
      if (!accessToken || !userEmail) {
        throw new Error("Authentication not ready");
      }

      const response = await fetch(`${API_URL}/orders/pdf/${orderId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `ordre_${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading PDF:", err);
      alert("Kunne ikke laste ned PDF. Prøv igjen senere.");
    }
  };

  useEffect(() => {
    if (accessToken && userEmail) {
      fetchOrders();
    }
  }, [accessToken, userEmail, fetchOrders]);

  if (loading) {
    return (
      <div className="order-history">
        <h1>Ordrehistorikk</h1>
        <div className="loading">Laster inn ordrehistorikk...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-history">
        <h1>Ordrehistorikk</h1>
        <div className="error">
          <p>{error}</p>
          <button onClick={fetchOrders} className="retry-btn">
            Prøv på nytt
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-history">
      <h1>Ordrehistorikk</h1>

      {orders.length === 0 ? (
        <div className="no-orders">
          <p>Du har ingen ordrer ennå.</p>
        </div>
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Ordrenummer</th>
                <th>Pakke</th>
                <th>Betalingsstatus</th>
                <th>Betalt NOK</th>
                <th>Dato</th>
                <th>Handling</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="order-id">#{order.id}</td>
                  <td className="order-title">{order.title}</td>
                  <td className="order-status">
                    <span
                      className={`status-badge status-${order.payment_status}`}
                    >
                      {translatePaymentStatus(order.payment_status)}
                    </span>
                  </td>
                  <td className="order-amount">
                    {formatAmount(order.amount_paid)}
                  </td>
                  <td className="order-date">{formatDate(order.created_at)}</td>
                  <td className="order-action">
                    <button
                      onClick={() => downloadPDF(order.id)}
                      className="download-btn"
                      disabled={order.payment_status !== "paid"}
                      title={
                        order.payment_status !== "paid"
                          ? "Kun tilgjengelig for betalte ordrer"
                          : ""
                      }
                    >
                      Last ned PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;
